import * as React from "react"

import Button, { type ButtonProps } from "#React/ui/Button"
import { cn } from "#Utils/cn"

type ButtonGroupContextValue = {
  selected: string | number | null
  onChange: (value: string | number | null) => void
  disabled: boolean
}

const ButtonGroupContext = React.createContext<ButtonGroupContextValue | null>(null)

type ButtonGroupProps = Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> & {
  selected?: any
  onChange?: (value: any) => void
  disabled?: boolean
}

const ButtonGroup = ({
  className,
  children,
  selected,
  onChange,
  disabled = false,
  ...props
}: ButtonGroupProps) => {
  const content = (
    <div className={cn("c-button-group", className)} {...props}>
      {children}
    </div>
  )

  if (onChange) {
    return (
      <ButtonGroupContext.Provider value={{ selected: selected ?? null, onChange, disabled }}>
        {content}
      </ButtonGroupContext.Provider>
    )
  }

  return content
}

type ItemProps = ButtonProps & {
  active?: boolean
  value?: string | number
}

const Item = ({ active, value, className, onClick, disabled, ...props }: ItemProps) => {
  const context = React.useContext(ButtonGroupContext)

  const isActive = context ? context.selected == value : active
  const isDisabled = disabled || context?.disabled

  const handleClick = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    context?.onChange(value || null)
    onClick?.(e)
  }

  return (
    <Button
      color="white"
      size="sm"
      disabled={isDisabled}
      {...props}
      onClick={handleClick}
      className={cn(
        "c-button-group__item",
        isActive ? "c-button--white-active" : "c-button--white-inactive",
        className,
      )}
    />
  )
}

ButtonGroup.Item = Item

export default ButtonGroup
